import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const BehindTheScenes: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Platform News • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Behind the Scenes: How IndeeClips Works
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Ever wonder what happens when you click "Upload Video"? How does IndeeClips deliver smooth streaming to millions of users? Why do some videos get recommended while others don't? Pull back the curtain and see the technology, team, and infrastructure powering India's fastest-growing video platform.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What Happens When You Upload a Video
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          You hit "Upload"—then what? A 60-second video triggers a complex chain of processes:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 1: Upload and Storage (0-30 seconds)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Your video uploads directly to Indian cloud servers (Mumbai and Bangalore data centers)</li>
          <li>We create a unique identifier (video ID) and link it to your channel</li>
          <li>Original file stored in AWS S3-compatible storage with triple redundancy</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 2: Video Processing (30 seconds - 5 minutes)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Transcoding:</strong> Convert your video into multiple quality levels (360p, 480p, 720p, 1080p) so viewers on slow internet can still watch</li>
          <li><strong>Thumbnail generation:</strong> Extract 3 frames as auto-thumbnails (you can upload custom ones)</li>
          <li><strong>Audio normalization:</strong> Adjust volume levels so your video isn't too loud or too quiet</li>
          <li><strong>Metadata extraction:</strong> Duration, resolution, bitrate, codec information</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 3: Content Checks (Automated, ~10 seconds)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Copyright detection:</strong> Audio fingerprinting checks if your video uses copyrighted music</li>
          <li><strong>Content moderation:</strong> AI scans for explicit/violent/hateful content (flagged videos reviewed by humans)</li>
          <li><strong>Spam filters:</strong> Detect keyword stuffing, misleading tags, or duplicate uploads</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 4: Distribution (Instant)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Video copied to CDN (Content Delivery Network) edge servers across India</li>
          <li>Subscribers notified (if they have notifications enabled)</li>
          <li>Video enters recommendation algorithm pool</li>
          <li>Indexed for search</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Total time from upload to watchable: 1-5 minutes for most videos. 4K or very long videos may take 10-15 minutes.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How Streaming Works
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          You click play. The video loads instantly, no buffering. Here's the magic behind smooth playback:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Adaptive Bitrate Streaming
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips doesn't send the entire video at once. Instead:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Video split into 4-second chunks</li>
          <li>Your device requests chunks as needed (only loads ~20 seconds ahead)</li>
          <li>If your internet slows down mid-video, the player automatically switches to a lower quality to prevent buffering</li>
          <li>When speed improves, quality ramps back up</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          CDN (Content Delivery Network)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Videos aren't served from one central server. We use a CDN with edge servers in:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Mumbai, Delhi, Bangalore, Hyderabad, Chennai, Kolkata (India)</li>
          <li>When you watch, the video comes from the geographically closest server—reducing latency and load times</li>
          <li>Popular videos cached at edge servers; less-popular videos fetched from origin storage</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Recommendation Algorithm Explained
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The homepage isn't random. An algorithm decides what you see. Here's how it works (simplified):
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Factors That Influence Recommendations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watch history:</strong> Videos you've watched recently influence what's recommended next</li>
          <li><strong>Watch time:</strong> Videos where viewers watch 70%+ are weighted higher</li>
          <li><strong>Engagement:</strong> Likes, comments, shares signal quality</li>
          <li><strong>Click-through rate (CTR):</strong> If your thumbnail/title gets clicks when shown, your video gets recommended more</li>
          <li><strong>Freshness:</strong> New videos get a temporary boost to give them initial exposure</li>
          <li><strong>Channel history:</strong> Channels with consistently high engagement get algorithmic favor</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Doesn't Affect Recommendations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Number of subscribers:</strong> Small channels can go viral—the algorithm doesn't favor big creators</li>
          <li><strong>Upload frequency:</strong> Posting daily doesn't boost your videos if quality suffers</li>
          <li><strong>Video length:</strong> Short and long videos compete equally (though retention rates differ)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How We're Different from YouTube
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          IndeeClips prioritizes regional content. If you watch Hindi videos, you'll see more Hindi creators—not just English-language viral trends. Our algorithm actively promotes lesser-known Indian creators instead of defaulting to global viral content.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Team Behind IndeeClips
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We're not a faceless corporation. Here's who builds and runs IndeeClips:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Engineering team:</strong> 12 developers (backend, frontend, mobile, infrastructure)</li>
          <li><strong>Content moderation:</strong> 8-person team reviews flagged videos (AI pre-filters, humans make final calls)</li>
          <li><strong>Creator support:</strong> 4 people handling creator emails, technical issues, partnership inquiries</li>
          <li><strong>Product/Design:</strong> 3 people deciding what features to build and how they should work</li>
          <li><strong>Operations:</strong> 2 people managing servers, CDN, databases, monitoring uptime</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Total team size: ~30 people. Compare that to YouTube's thousands—we're lean, fast, and creator-focused.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Infrastructure Costs (The Economics)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Running a video platform is expensive. Transparency about costs helps creators understand why monetization thresholds exist.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Storage:</strong> ~₹2 per GB/month (10,000 hours of video = ~5TB = ₹10,000/month)</li>
          <li><strong>Bandwidth:</strong> ~₹5 per GB transferred (1 million views of a 100MB video = ₹500,000 bandwidth cost)</li>
          <li><strong>Transcoding:</strong> ~₹0.50 per minute of video processed</li>
          <li><strong>CDN:</strong> Additional ₹3-₹4 per GB for edge caching</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          A single viral video (10M views, 5 min, 720p) costs us ~₹200,000 in bandwidth alone. Ads cover this, but it's why free platforms struggle without massive scale.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Content Moderation Process
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          How do we keep IndeeClips safe while respecting free expression?
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Automated Filtering
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>AI scans every upload for nudity, graphic violence, hate symbols</li>
          <li>90% accuracy—catches obvious violations instantly</li>
          <li>Borderline content flagged for human review</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Human Review
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Moderation team reviews flagged content within 24 hours</li>
          <li>Context matters—educational content about sensitive topics allowed, gratuitous violations removed</li>
          <li>Creators can appeal removals—appeals reviewed by senior moderators</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Security and Privacy
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Data encryption:</strong> All video uploads and user data encrypted in transit (HTTPS) and at rest (AES-256)</li>
          <li><strong>Password security:</strong> Passwords hashed with bcrypt—we can't see your password, even if we wanted to</li>
          <li><strong>No data selling:</strong> We don't sell user data to third parties (our business model is ads, not data brokering)</li>
          <li><strong>India-first compliance:</strong> Servers in India, compliant with IT Act 2000 and GDPR principles</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Transparency Matters
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Most platforms hide how they work. We believe creators and viewers deserve to understand the infrastructure powering their content. If you have questions about how IndeeClips operates—technical, financial, or policy-related—we're happy to answer.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Ask us anything:{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default BehindTheScenes;
